import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { IconButton, Tooltip } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import ColorPill from 'core-system/Pill/ColorPill'
import Dropdown from 'core-system/Dropdown/Dropdown'
import FileUpload from 'core-system/FileUpload/FileUpload'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import TableOperations from 'core-system/Table/TableOperationsUI'
import { FilterCheckbox, FilterObject } from 'core-system/Table/FilterModel'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { Locations } from 'shared/Router/Locations'
import ReportedUsageService from './reportedUsageService'
import {
  fileExtension,
  filterUploads,
  formatUploadedDate,
  matchRate,
  merchantLabel,
  programMerchantChoices,
  statusPill,
  UploadHistoryRow,
} from './reportedUsageUpload'

const Container = styled.div`
  padding: 2rem;
`

const BackLink = styled(Link)`
  color: ${palette.primary.pink700};
  text-decoration: none;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`

const Subtitle = styled(Text)`
  margin-top: 0.75rem;
`

const Card = styled.div`
  margin: 1.5rem 0 2rem;
  padding: 1.25rem 1.5rem 1.5rem;
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  background: ${palette.white};
`

const CardHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`

const Dropzone = styled.div`
  margin-top: 1rem;
`

const UploadError = styled(Text)`
  margin-top: 0.75rem;
`

const HistoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`

const HistoryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const Count = styled.span`
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  background: ${palette.grey.grey100};
  color: ${palette.grey.grey700};
  font-size: 0.75rem;
  font-weight: 500;
`

const FileCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
`

const Extension = styled.span`
  flex-shrink: 0;
  padding: 0.15rem 0.4rem;
  border-radius: 0.35rem;
  background: ${palette.grey.grey100};
  color: ${palette.grey.grey700};
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.04em;
`

const FileName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 16rem;
`

const Match = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 7.5rem;
`

const Track = styled.div`
  flex: 1;
  height: 0.25rem;
  border-radius: 999px;
  background: ${palette.grey.grey100};
  overflow: hidden;
`

const Fill = styled.div<{ $width: number }>`
  width: ${(props) => props.$width}%;
  height: 100%;
  border-radius: 999px;
  background: ${palette.success.green500};
`

const HeaderCell = ({ text }: { text: string }) => (
  <TableRowCell noWrap>
    <Text variant='action4' fontWeight={500} color={palette.text.secondary}>
      {text}
    </Text>
  </TableRowCell>
)

const RowCell = ({ children }: { children: React.ReactNode }) => (
  <TableRowCell style={{ padding: '1rem' }}>
    <Text fontWeight={400} variant='action3'>
      {children}
    </Text>
  </TableRowCell>
)

const ReportedUsageDataView = () => {
  const { launchId: rawLaunchId } = useParams<{ launchId: string }>()
  const launchId = rawLaunchId ? decodeURIComponent(rawLaunchId) : ''
  const [merchant, setMerchant] = useState('')
  const [programMerchants, setProgramMerchants] = useState<string[]>([])
  const [uploads, setUploads] = useState<UploadHistoryRow[]>([])
  const [error, setError] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [selectedFilters, setSelectedFilters] = useState<FilterCheckbox[]>([])
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null
  )

  const merchantChoices = useMemo(
    () => programMerchantChoices(programMerchants),
    [programMerchants]
  )

  const load = async () => {
    const uploadResponse = await ReportedUsageService.listUploads(launchId)
    const body = uploadResponse.data || {}
    const allowed = (body.merchants || []) as string[]
    setProgramMerchants(allowed)
    setMerchant((current) =>
      allowed.includes(current) ? current : allowed[0] || ''
    )
    const records = (body.uploads || []) as Array<
      Omit<UploadHistoryRow, 'merchantName' | 'statusText' | 'uploadedOn'>
    >
    setUploads(
      records.map((upload) => ({
        ...upload,
        merchantName: merchantLabel(upload.merchant),
        statusText: statusPill(upload.status).text,
        uploadedOn: formatUploadedDate(upload.createdAt),
        parsedCount: upload.parsedCount || 0,
        matchedCount: upload.matchedCount || 0,
        createdAt: upload.createdAt || '',
      }))
    )
  }

  useEffect(() => {
    if (launchId) {
      load().catch(() => setError('Could not load uploaded data.'))
    }
  }, [launchId])

  const uploadFiles = async (files: globalThis.File[]) => {
    const file = files[0]
    if (!file) {
      return
    }
    setError('')
    try {
      await ReportedUsageService.uploadFile(launchId, merchant, file)
      await load()
    } catch (err: any) {
      throw new Error(err?.response?.data?.detail || 'Upload failed.')
    }
  }

  const filters = useMemo<FilterObject[]>(() => {
    const merchants = [
      ...new Map(uploads.map((upload) => [upload.merchant, upload.merchantName])),
    ]
    const statuses = [
      ...new Map(uploads.map((upload) => [upload.status, upload.statusText])),
    ]
    const sections: FilterObject[] = []
    if (merchants.length) {
      sections.push({
        header: 'Merchant',
        checkboxes: merchants.map(([id, label]) => ({
          label,
          checked: false,
          key: 'merchant',
          value: id,
        })),
      })
    }
    if (statuses.length) {
      sections.push({
        header: 'Status',
        checkboxes: statuses.map(([id, label]) => ({
          label,
          checked: false,
          key: 'status',
          value: id,
        })),
      })
    }
    return sections
  }, [uploads])

  const visibleUploads = useMemo(
    () =>
      filterUploads(
        uploads,
        appliedSearch,
        selectedFilters.map((item) => ({
          key: item.key,
          value: String(item.value),
        })),
        sortDirection
      ),
    [appliedSearch, selectedFilters, sortDirection, uploads]
  )

  const download = async (uploadId: string) => {
    const response = await ReportedUsageService.downloadUpload(uploadId)
    if (response.data?.url) {
      window.open(response.data.url, '_blank', 'noopener')
    }
  }

  return (
    <Container>
      <BackLink
        to={Locations.DashboardProgram.replace(
          ':launchId',
          encodeURIComponent(launchId)
        )}
      >
        Back to program
      </BackLink>
      <Subtitle variant='body1' color={palette.text.secondary}>
        Upload merchant files to match trips and spend to enrolled commuters.
      </Subtitle>
      <Card>
        <CardHeader>
          <Text variant='h4' fontWeight={600}>
            Upload a file
          </Text>
          <Text variant='caption' color={palette.text.secondary}>
            CSV or XLSX · up to 50 MB
          </Text>
        </CardHeader>
        <Dropdown
          label='Merchant'
          variant='contained'
          active={merchant}
          items={merchantChoices.map((item) => ({
            id: item.id,
            text: item.label,
          }))}
          itemCallback={(item) => setMerchant(item.id)}
          disabled={!merchantChoices.length}
        />
        <Dropzone>
        <FileUpload
          placeholder='Choose a file or drag it here'
          placeholderSubtitle='CSV or XLSX'
          acceptedFilesTypes={['.csv', '.xlsx']}
          maxSize={50}
          files={[]}
          onChange={() => undefined}
          uploadFiles={uploadFiles}
          onError={setError}
        />
        </Dropzone>
        {error ? (
          <UploadError variant='body2' color={palette.error.red500}>
            {error}
          </UploadError>
        ) : null}
      </Card>
      <HistoryHeader>
        <HistoryTitle>
          <Text variant='h3' fontWeight={600}>
            Upload history
          </Text>
          <Count>
            {visibleUploads.length}{' '}
            {visibleUploads.length === 1 ? 'file' : 'files'}
          </Count>
        </HistoryTitle>
        {uploads.length ? (
          <TableOperations
            handleSearch={(value) => setAppliedSearch(value.trim())}
            handleSearchClear={() => {
              setSearchValue('')
              setAppliedSearch('')
            }}
            onAscendingClick={() => setSortDirection('asc')}
            onDescendingClick={() => setSortDirection('desc')}
            filters={filters}
            handleApplyFilters={setSelectedFilters}
            handleFiltersReset={() => setSelectedFilters([])}
            isFilterButtonActive={selectedFilters.length > 0}
            activeSortDirection={sortDirection}
            isSearchActive={Boolean(appliedSearch)}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        ) : null}
      </HistoryHeader>
      <Table
        maxRows={10}
        emptyMessage={
          uploads.length ? 'No files match' : 'No files uploaded yet'
        }
        showPagination={visibleUploads.length > 10}
        headerCells={[
          <HeaderCell key='file' text='File' />,
          <HeaderCell key='uploaded' text='Uploaded' />,
          <HeaderCell key='merchant' text='Merchant' />,
          <HeaderCell key='status' text='Status' />,
          <HeaderCell key='rows' text='Rows' />,
          <HeaderCell key='match' text='Match rate' />,
          <HeaderCell key='actions' text='' />,
        ]}
        rows={visibleUploads.map((upload) => {
          const status = statusPill(upload.status)
          const rate = matchRate(upload.matchedCount, upload.parsedCount)
          return (
            <TableRow key={upload.id}>
              <RowCell>
                <FileCell>
                  <Extension>{fileExtension(upload.originalFilename)}</Extension>
                  <FileName title={upload.originalFilename}>
                    {upload.originalFilename}
                  </FileName>
                </FileCell>
              </RowCell>
              <TableRowCell noWrap style={{ padding: '1rem' }}>
                <Text fontWeight={400} variant='action3'>
                  {upload.uploadedOn || '—'}
                </Text>
              </TableRowCell>
              <RowCell>{upload.merchantName}</RowCell>
              <TableRowCell style={{ padding: '1rem' }}>
                <ColorPill color={status.color} text={status.text} />
              </TableRowCell>
              <RowCell>{(upload.parsedCount || 0).toLocaleString('en-US')}</RowCell>
              <RowCell>
                <Match>
                  <Track>
                    <Fill $width={rate} />
                  </Track>
                  {rate}%
                </Match>
              </RowCell>
              <TableRowCell style={{ padding: '0.25rem 1rem' }}>
                <Tooltip title='Download'>
                  <IconButton
                    aria-label={`Download ${upload.originalFilename}`}
                    onClick={() => download(upload.id)}
                    size='small'
                  >
                    <DownloadIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </TableRowCell>
            </TableRow>
          )
        })}
      />
    </Container>
  )
}

export default ReportedUsageDataView
